import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Plus, Trash2, Search, Building2, Package, X, Loader2, FileText, CheckCircle, Truck, AlertTriangle, Send, DollarSign, Banknote, CreditCard, ArrowRight } from 'lucide-react';
import { comprasService } from '@/services/compras.service';
import { proveedoresService } from '@/services/proveedores.service';
import { productosService } from '@/services/productos.service';
import { pagosProveedoresService } from '@/services/pagos-proveedores.service';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';
import { dialogs } from '@/stores/dialog-store';

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  DRAFT: { label: 'Borrador', color: 'bg-ios-gray-6 text-ios-gray', icon: FileText },
  PENDING: { label: 'Pendiente', color: 'bg-yellow-50 text-yellow-600', icon: Send },
  APPROVED: { label: 'Aprobada', color: 'bg-ios-blue/10 text-ios-blue', icon: CheckCircle },
  RECEIVED: { label: 'Recibida', color: 'bg-ios-green/10 text-ios-green', icon: Truck },
  CANCELLED: { label: 'Cancelada', color: 'bg-red-50 text-ios-red', icon: AlertTriangle },
};

export function PurchaseOrderFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { handleAuthRedirect } = useApiError();
  const isViewing = !!id && id !== 'new';
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderStatus, setOrderStatus] = useState('');

  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [notes, setNotes] = useState('');
  const [folio, setFolio] = useState('');

  // Payments
  const [payments, setPayments] = useState<any[]>([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, paymentMethod: 'TRANSFER', reference: '', notes: '' });
  const [paymentSaving, setPaymentSaving] = useState(false);

  // Items
  const [items, setItems] = useState<Array<{ productId: string; name: string; sku: string; quantity: number; unitPrice: number }>>([]);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (!user?.company?.id) return;
    proveedoresService.findAll(user.company.id).then(setSuppliers).catch(() => {});
  }, [user?.company?.id]);

  useEffect(() => {
    if (isViewing) loadOrder();
  }, [id]);

  const loadOrder = async () => {
    setLoading(true);
    try {
      const order = await comprasService.findById(id!);
      setFolio(order.folio);
      setOrderStatus(order.status);
      setSupplierId(order.supplierId);
      setNotes(order.notes || '');
      setItems(order.items.map((i: any) => ({
        productId: i.productId,
        name: i.product?.name || '',
        sku: i.product?.sku || '',
        quantity: i.quantity,
        unitPrice: Number(i.unitPrice),
      })));

      // Load payments for this order
      try {
        const paymentsResult = await pagosProveedoresService.findAll({
          companyId: user!.company!.id,
          purchaseOrderId: id!,
          limit: 50,
        });
        setPayments(paymentsResult.data || []);
      } catch { /* empty */ }
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError('Error al cargar orden');
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async (search: string) => {
    if (!user?.company?.id) return;
    setSearchLoading(true);
    try {
      const result = await productosService.findAll({ companyId: user.company.id, search, limit: 20 });
      setProductResults(result.data || []);
    } catch { /* empty */ }
    setSearchLoading(false);
  };

  const addProduct = async (product: any) => {
    if (items.find(i => i.productId === product.id)) return;
    setItems(prev => [...prev, {
      productId: product.id,
      name: product.name,
      sku: product.sku,
      quantity: 1,
      unitPrice: Number(product.costLast) || Number(product.costAverage) || 0,
    }]);
    setShowProductSearch(false);
    setProductSearch('');
  };

  const removeItem = async (productId: string) => {
    setItems(prev => prev.filter(i => i.productId !== productId));
  };

  const updateItem = async (productId: string, field: string, value: number) => {
    setItems(prev => prev.map(i => i.productId === productId ? { ...i, [field]: value } : i));
  };

  const subtotal = items.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  const taxTotal = subtotal * 0.16;
  const grandTotal = subtotal + taxTotal;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.company?.id || !user?.branch?.id || !supplierId || items.length === 0) return;
    setSaving(true);
    setError('');
    try {
      await comprasService.create({
        companyId: user.company.id,
        branchId: user.branch.id,
        supplierId,
        notes: notes || undefined,
        items: items.map(i => ({
          productId: i.productId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          subtotal: i.quantity * i.unitPrice,
        })),
        grandTotal,
        userId: user.id,
        createdById: user.id,
      });
      navigate('/purchases');
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError(err.response?.data?.message || 'Error al crear orden de compra');
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (action: 'submit' | 'approve' | 'receive' | 'cancel') => {
    if (!id) return;
    setSaving(true);
    setError('');
    try {
      if (action === 'cancel' && !await dialogs.confirm('¿Cancelar esta orden de compra?')) { setSaving(false); return; }
      if (action === 'receive' && !await dialogs.confirm('¿Recibir esta orden? Se actualizará el inventario.')) { setSaving(false); return; }

      switch (action) {
        case 'submit': await comprasService.submit(id); break;
        case 'approve': await comprasService.approve(id); break;
        case 'receive': await comprasService.receive(id); break;
        case 'cancel': await comprasService.cancel(id, 'Cancelación manual'); break;
      }
      loadOrder();
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      const labels: Record<string, string> = {
        submit: 'enviar', approve: 'aprobar', receive: 'recibir', cancel: 'cancelar',
      };
      setError(
        err.response?.data?.message
        || err.response?.data?.error
        || `Error al ${labels[action] || action} la orden`
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user?.branch?.id) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin" />
      </div>
    );
  }

  // ========= DETAIL VIEW for existing orders =========
  if (isViewing) {
    const StatusIcon = statusConfig[orderStatus]?.icon || FileText;
    return (
      <div className="w-full space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/purchases')} className="p-2 rounded-lg hover:bg-ios-gray-6 transition-colors">
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">Orden {folio}</h1>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig[orderStatus]?.color || ''}`}>
                  <StatusIcon size={12} />
                  {statusConfig[orderStatus]?.label || orderStatus}
                </span>
              </div>
              <p className="text-sm text-ios-gray mt-1">Detalle de la orden de compra</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-ios-red text-sm p-4 rounded-[12px] border border-red-100">{error}</div>
        )}

        {/* Supplier Info */}
        <div className="ios-card p-6 space-y-4">
          <h2 className="font-semibold text-base flex items-center gap-2">
            <Building2 size={18} className="text-ios-blue" />
            Proveedor
          </h2>
          <p className="text-sm font-medium">
            {suppliers.find(s => s.id === supplierId)?.name || 'Cargando...'}
          </p>
          {notes && <p className="text-sm text-ios-gray mt-1">Notas: {notes}</p>}
        </div>

        {/* Products */}
        <div className="ios-card p-6 space-y-4">
          <h2 className="font-semibold text-base flex items-center gap-2">
            <Package size={18} className="text-ios-blue" />
            Productos
            <span className="text-xs bg-ios-blue text-white px-1.5 py-0.5 rounded-full">{items.length}</span>
          </h2>

          <div className="space-y-2">
            {items.map((item) => (
              <div key={item.productId} className="flex items-center gap-3 bg-ios-gray-6 rounded-[10px] p-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.name}</p>
                  <p className="text-[11px] text-ios-gray">{item.sku}</p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-ios-gray">{item.quantity} × ${item.unitPrice.toFixed(2)}</span>
                  <span className="font-semibold w-[70px] text-right">${(item.quantity * item.unitPrice).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-ios-gray-5 pt-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-ios-gray">Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-ios-gray">IVA (16%)</span>
              <span>${taxTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-base font-bold pt-2 border-t border-ios-gray-5">
              <span>Total</span>
              <span className="text-ios-blue">${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Payments Section */}
        {orderStatus === 'RECEIVED' && (
          <div className="ios-card p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-semibold text-base flex items-center gap-2">
                <DollarSign size={18} className="text-ios-green" />
                Pago a proveedor
              </h2>
              {(() => {
                const paid = payments.reduce((s, p) => s + Number(p.amount), 0);
                const remaining = Math.max(0, grandTotal - paid);
                if (remaining <= 0.009) {
                  return (
                    <span className="text-xs bg-ios-green/10 text-ios-green px-2 py-1 rounded-full font-medium">
                      Liquidada (${paid.toFixed(2)})
                    </span>
                  );
                }
                return (
                  <span className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-full font-medium">
                    Por pagar: ${remaining.toFixed(2)}
                  </span>
                );
              })()}
            </div>

            {(() => {
              const paid = payments.reduce((s, p) => s + Number(p.amount), 0);
              const remaining = Math.max(0, grandTotal - paid);
              if (remaining > 0.009 && !showPaymentForm) {
                return (
                  <div className="bg-amber-50 border border-amber-100 rounded-[12px] p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-medium text-amber-900">Esta compra aún no está pagada</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Debes ${remaining.toFixed(2)} a {suppliers.find(s => s.id === supplierId)?.name || 'proveedor'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setPaymentForm(f => ({ ...f, amount: remaining }));
                        setShowPaymentForm(true);
                      }}
                      className="ios-button text-sm px-4 py-2 flex items-center gap-1"
                    >
                      <Plus size={14} /> Pagar ahora
                    </button>
                  </div>
                );
              }
              return null;
            })()}

            <div className="flex items-center justify-between">
              <span className="text-xs text-ios-gray">
                ${payments.reduce((s, p) => s + Number(p.amount), 0).toFixed(2)} pagados de ${grandTotal.toFixed(2)}
              </span>
              {!showPaymentForm && grandTotal - payments.reduce((s, p) => s + Number(p.amount), 0) > 0.009 && (
                <button onClick={() => {
                  const remaining = grandTotal - payments.reduce((s, p) => s + Number(p.amount), 0);
                  setPaymentForm(f => ({ ...f, amount: remaining > 0 ? remaining : grandTotal }));
                  setShowPaymentForm(true);
                }} className="text-sm text-ios-blue hover:underline flex items-center gap-1">
                  <Plus size={14} /> Registrar otro pago
                </button>
              )}
            </div>

            {/* Payment Form */}
            {showPaymentForm && (
              <div className="bg-ios-gray-6 rounded-[12px] p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-ios-gray mb-1 block">Monto</label>
                    <input type="number" value={paymentForm.amount}
                      onChange={(e) => setPaymentForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))}
                      className="ios-input w-full text-sm" step="0.01" min="0.01" autoFocus />
                  </div>
                  <div>
                    <label className="text-xs text-ios-gray mb-1 block">Método</label>
                    <select value={paymentForm.paymentMethod}
                      onChange={(e) => setPaymentForm(f => ({ ...f, paymentMethod: e.target.value }))}
                      className="ios-input w-full text-sm">
                      <option value="TRANSFER">Transferencia</option>
                      <option value="CASH">Efectivo</option>
                      <option value="CARD">Tarjeta</option>
                      <option value="CREDIT">Crédito</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-ios-gray mb-1 block">Referencia</label>
                    <input type="text" value={paymentForm.reference}
                      onChange={(e) => setPaymentForm(f => ({ ...f, reference: e.target.value }))}
                      className="ios-input w-full text-sm" placeholder="SPEI-XXXXX" />
                  </div>
                  <div>
                    <label className="text-xs text-ios-gray mb-1 block">Notas</label>
                    <input type="text" value={paymentForm.notes}
                      onChange={(e) => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                      className="ios-input w-full text-sm" placeholder="Opcional" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={async () => {
                    if (!user?.company?.id || !supplierId || paymentForm.amount <= 0) return;
                    const paid = payments.reduce((s, p) => s + Number(p.amount), 0);
                    const remaining = Math.max(0, grandTotal - paid);
                    if (paymentForm.amount > remaining + 0.009) {
                      await dialogs.alert(`El pago no puede superar el saldo pendiente de $${remaining.toFixed(2)}.`);
                      return;
                    }
                    setPaymentSaving(true);
                    try {
                      await pagosProveedoresService.create({
                        companyId: user.company.id,
                        supplierId,
                        purchaseOrderId: id,
                        amount: paymentForm.amount,
                        paymentMethod: paymentForm.paymentMethod,
                        reference: paymentForm.reference || undefined,
                        notes: paymentForm.notes || undefined,
                      });
                      setShowPaymentForm(false);
                      setPaymentForm({ amount: 0, paymentMethod: 'TRANSFER', reference: '', notes: '' });
                      // Reload payments
                      const result = await pagosProveedoresService.findAll({
                        companyId: user.company.id, purchaseOrderId: id, limit: 50,
                      });
                      setPayments(result.data || []);
                    } catch { await dialogs.alert('Error al registrar pago'); }
                    setPaymentSaving(false);
                  }} disabled={paymentSaving || paymentForm.amount <= 0} className="ios-button text-sm">
                    {paymentSaving ? 'Guardando...' : 'Guardar Pago'}
                  </button>
                  <button onClick={() => setShowPaymentForm(false)} className="px-3 py-2 text-sm text-ios-gray hover:text-foreground">
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Payments list */}
            {payments.length > 0 && (
              <div className="space-y-1.5">
                {payments.map((p: any) => {
                  const Icon = p.paymentMethod === 'CASH' ? Banknote : p.paymentMethod === 'CARD' ? CreditCard : p.paymentMethod === 'TRANSFER' ? ArrowRight : DollarSign;
                  const paid = Number(p.amount);
                  const total = grandTotal;
                  const pct = total > 0 ? (paid / total * 100) : 0;
                  return (
                    <div key={p.id} className="flex items-center gap-3 bg-ios-gray-6 rounded-[10px] p-3">
                      <div className="w-8 h-8 bg-ios-green/10 rounded-[8px] flex items-center justify-center flex-shrink-0">
                        <Icon size={14} className="text-ios-green" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium">
                          ${paid.toFixed(2)} · {p.paymentMethod === 'CASH' ? 'Efectivo' : p.paymentMethod === 'CARD' ? 'Tarjeta' : p.paymentMethod === 'TRANSFER' ? 'Transferencia' : 'Crédito'}
                        </p>
                        <p className="text-[10px] text-ios-gray">
                          {new Date(p.paidAt).toLocaleDateString('es-MX')}
                          {p.reference && ` · Ref: ${p.reference}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] text-ios-gray block">{pct.toFixed(0)}%</span>
                        <div className="w-16 h-1.5 bg-ios-gray-4 rounded-full overflow-hidden mt-0.5">
                          <div className="h-full bg-ios-green rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {payments.length === 0 && !showPaymentForm && (
              <p className="text-sm text-ios-gray text-center py-4">Sin pagos registrados</p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 justify-end">
          {orderStatus === 'DRAFT' && (
            <button onClick={() => handleAction('submit')} disabled={saving} className="ios-button flex items-center gap-2">
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              Enviar a Aprobación
            </button>
          )}
          {orderStatus === 'PENDING' && (
            <>
              <button onClick={() => handleAction('approve')} disabled={saving} className="ios-button bg-ios-blue flex items-center gap-2">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                Aprobar
              </button>
              <button onClick={() => handleAction('cancel')} disabled={saving} className="px-4 py-2.5 rounded-[10px] text-sm border border-red-200 text-ios-red hover:bg-red-50 flex items-center gap-2">
                <AlertTriangle size={16} /> Cancelar
              </button>
            </>
          )}
          {orderStatus === 'APPROVED' && (
            <>
              <button onClick={() => handleAction('receive')} disabled={saving} className="ios-button bg-ios-green flex items-center gap-2">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Truck size={18} />}
                Recibir Mercancía
              </button>
              <button onClick={() => handleAction('cancel')} disabled={saving} className="px-4 py-2.5 rounded-[10px] text-sm border border-red-200 text-ios-red hover:bg-red-50 flex items-center gap-2">
                <AlertTriangle size={16} /> Cancelar
              </button>
            </>
          )}
          {orderStatus === 'RECEIVED' && (
            <span className="text-sm text-ios-green bg-ios-green/10 px-4 py-2 rounded-[10px] font-medium flex items-center gap-2">
              <CheckCircle size={16} /> Recibida completamente
            </span>
          )}
          {orderStatus === 'CANCELLED' && (
            <span className="text-sm text-ios-red bg-red-50 px-4 py-2 rounded-[10px] font-medium flex items-center gap-2">
              <AlertTriangle size={16} /> Orden cancelada
            </span>
          )}
        </div>
      </div>
    );
  }

  // ========= CREATE FORM for new orders =========
  return (
    <div className="w-full space-y-4">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/purchases')} className="p-2 rounded-lg hover:bg-ios-gray-6 transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Nueva Orden de Compra</h1>
          <p className="text-sm text-ios-gray mt-1">Crea una orden de compra para tu proveedor</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-ios-red text-sm p-4 rounded-[12px] border border-red-100">{error}</div>
      )}

      <form onSubmit={handleCreate} className="space-y-6">
        {/* Supplier selection */}
        <div className="ios-card p-6 space-y-4">
          <h2 className="font-semibold text-base flex items-center gap-2">
            <Building2 size={18} className="text-ios-blue" />
            Proveedor
          </h2>
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="ios-input w-full" required>
            <option value="">Seleccionar proveedor...</option>
            {suppliers.map((s: any) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Notas</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="ios-input w-full min-h-[60px] resize-none" placeholder="Notas para la orden de compra..." />
          </div>
        </div>

        {/* Products */}
        <div className="ios-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <Package size={18} className="text-ios-blue" />
              Productos
              {items.length > 0 && <span className="text-xs bg-ios-blue text-white px-1.5 py-0.5 rounded-full">{items.length}</span>}
            </h2>
            <button type="button" onClick={() => setShowProductSearch(true)} className="text-sm text-ios-blue hover:underline flex items-center gap-1">
              <Plus size={14} /> Agregar producto
            </button>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-8 text-ios-gray">
              <Package size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Agrega productos a la orden</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item.productId} className="flex items-center gap-3 bg-ios-gray-6 rounded-[10px] p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.name}</p>
                    <p className="text-[11px] text-ios-gray">{item.sku}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div>
                      <label className="text-[10px] text-ios-gray block">Cant.</label>
                      <input type="number" value={item.quantity} onChange={(e) => updateItem(item.productId, 'quantity', parseInt(e.target.value) || 0)} className="ios-input w-16 text-xs text-center" min="1" />
                    </div>
                    <div>
                      <label className="text-[10px] text-ios-gray block">P/U</label>
                      <input type="number" value={item.unitPrice} onChange={(e) => updateItem(item.productId, 'unitPrice', parseFloat(e.target.value) || 0)} className="ios-input w-20 text-xs text-right" step="0.01" min="0" />
                    </div>
                    <div className="text-right min-w-[60px]">
                      <label className="text-[10px] text-ios-gray block">Subtotal</label>
                      <p className="text-xs font-semibold">${(item.quantity * item.unitPrice).toFixed(2)}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeItem(item.productId)} className="p-1.5 rounded-lg hover:bg-red-100 text-ios-gray hover:text-ios-red transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {items.length > 0 && (
            <div className="border-t border-ios-gray-5 pt-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-ios-gray">Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ios-gray">IVA (16%)</span>
                <span>${taxTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-base font-bold pt-2 border-t border-ios-gray-5">
                <span>Total</span>
                <span className="text-ios-blue">${grandTotal.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving || !supplierId || items.length === 0} className="ios-button flex items-center gap-2">
            {saving ? <><Loader2 size={18} className="animate-spin" /> Creando...</> : <><Save size={18} /> Crear Orden de Compra</>}
          </button>
        </div>
      </form>

      {/* Product Search Modal */}
      {showProductSearch && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-lg max-h-[80vh] flex flex-col shadow-ios-xl">
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold">Agregar Producto</h3>
              <button onClick={() => { setShowProductSearch(false); setProductSearch(''); }} className="p-2 rounded-lg hover:bg-ios-gray-6"><X size={18} /></button>
            </div>
            <div className="p-4">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
                <input type="text" value={productSearch} onChange={(e) => { setProductSearch(e.target.value); if (e.target.value.length > 1) searchProducts(e.target.value); }} placeholder="Buscar producto..." className="ios-input w-full pl-9 text-sm" autoFocus />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-1">
              {searchLoading ? (
                <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-ios-gray" /></div>
              ) : productResults.length === 0 ? (
                <p className="text-center text-sm text-ios-gray py-6">{productSearch.length > 1 ? 'Sin resultados' : 'Escribe para buscar...'}</p>
              ) : (
                productResults.map((p: any) => (
                  <button key={p.id} type="button" onClick={() => addProduct(p)} disabled={!!items.find(i => i.productId === p.id)} className="w-full text-left px-3 py-2.5 rounded-[10px] text-sm hover:bg-ios-blue/5 transition-colors flex items-center justify-between disabled:opacity-40">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{p.name}</p>
                      <p className="text-xs text-ios-gray">{p.sku} | ${Number(p.pricePublic).toFixed(2)}</p>
                    </div>
                    {items.find(i => i.productId === p.id) && <span className="text-[10px] text-ios-green font-medium">Agregado</span>}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
